"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import { resolveSfeerHotelImage } from "@/components/deal/hotelImage";
import { sfeerbeeldForHotel } from "@/lib/content/hotel-sfeerbeeld";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { formatRequiredPhotoCredit } from "@/lib/photo-credit";
import PhotoCreditMini from "@/components/deals/PhotoCreditMini";

/**
 * Hotel-impressions carousel for the deal page (Dennis 2026-06-03).
 * Prefers the operator-CURATED gallery photos (carousel_images with captions):
 * those are the hand-picked, nice shots, shown nicest-first. A curated photo
 * qualifies only when its caption matches one of the deal's hotel names, which
 * is also the join that supplies stars + city. Falls back to one auto-picked
 * photo per hotel when no curated photo qualifies —
 * Dennis flagged the auto first-photo-per-hotel as "serieus lelijk" (the raw
 * RateHawk first image is often a dark/blurry exterior).
 */

// A curated caption only counts as a hotel photo when it IS one of this
// deal's hotel names. Normalized (trim / collapsed whitespace / case) so a
// stray space or capital in the CMS caption still joins to its hotel instead
// of silently dropping the photo.
function normalizeHotelName(value) {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ").toLowerCase() : "";
}

// Two different hotels in one deal can carry the same name (a chain with a
// branch in two cities). A caption then cannot say which one it means, so the
// key is marked ambiguous and matches nothing: showing the wrong city/stars
// under a photo is worse than showing no slide.
const AMBIGUOUS = Symbol("ambiguous hotel name");

function claimName(map, key, hotel) {
  if (!key) return;
  const prev = map.get(key);
  if (prev === undefined) map.set(key, hotel);
  else if (prev !== AMBIGUOUS && (prev.hotel_id ?? null) !== (hotel.hotel_id ?? null)) {
    map.set(key, AMBIGUOUS);
  }
}

function resolveName(map, key) {
  const hit = key ? map.get(key) : undefined;
  return hit && hit !== AMBIGUOUS ? hit : null;
}

function Stars({ count = 3 }) {
  return (
    <span className="inline-flex align-middle">
      {[...Array(count)].map((_, i) => (
        <svg key={i} className="w-3.5 h-3.5 fill-yellow-400" viewBox="0 0 576 512">
          <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z" />
        </svg>
      ))}
    </span>
  );
}

// Fallback hotel list: with a non-empty `order` exactly those hotels in that
// order (ids that are not in `hotels` are skipped), else the hotels as given.
function fallbackHotels(hotels, order) {
  const list = hotels || [];
  if (!Array.isArray(order) || order.length === 0) return list;
  const byId = new Map(list.map((h) => [h?.hotel_id, h]));
  const seen = new Set();
  const out = [];
  for (const id of order) {
    const h = byId.get(id);
    if (!h || seen.has(id)) continue;
    seen.add(id);
    out.push(h);
  }
  return out;
}

export default function HotelImpressionsCarousel({ hotels = [], photos = [], leadHotelId = null, order = null }) {
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  // Maps, not plain objects: a caption like "constructor" or "toString" would
  // hit Object.prototype on a `{}` lookup and pass as a matched hotel.
  const byName = new Map();
  const byNormalizedName = new Map();
  for (const h of hotels || []) {
    // Guard on the normalized form: a whitespace-only hotel_name is truthy but
    // would join a whitespace-only caption and render an empty label.
    const key = normalizeHotelName(h?.hotel_name);
    if (!key) continue;
    claimName(byName, h.hotel_name, h);
    claimName(byNormalizedName, key, h);
  }
  // p.caption here is the NL/source caption (the page builder passes captionNl,
  // not the visitor-locale string) precisely so this join against the
  // un-localized hotel_name keeps matching. Do NOT swap it for a
  // locale-resolved caption or the stars/city join breaks on .de/.fr.
  // Exact spelling wins: it still resolves the one deal where two hotels differ
  // only in capitalization, which the normalized key has to call ambiguous.
  const hotelForCaption = (caption) =>
    resolveName(byName, caption) || resolveName(byNormalizedName, normalizeHotelName(caption));
  // A gallery photo is a curated HOTEL photo only when its caption resolves to
  // one of this deal's hotels. The caller cannot decide this — it has hotel ids
  // but no names — so the gate lives here, where the join data is. Captions that
  // are scenic sales lines (the 5-language caption backfill of 2026-07-20/21 put
  // those on plain /images/… upload tiles) would otherwise render AS the hotel
  // name, starless, on every locale. Unmatched captions stay in the normal
  // gallery; here they simply drop out, and an all-scenic set falls through to
  // the per-hotel fallback below rather than inventing hotel names.
  const curated = [];
  const seenHotel = new Set();
  for (const p of photos || []) {
    if (!p || !p.url) continue;
    const hotel = hotelForCaption(p.caption);
    if (!hotel) {
      // Dev-only: an operator whose caption is one character off (a curly
      // apostrophe, a trailing period) would otherwise see the photo vanish
      // from the carousel with no explanation anywhere.
      if (process.env.NODE_ENV !== "production" && p.caption) {
        console.warn(
          `[HotelImpressionsCarousel] caption is not a hotel name of this deal, photo skipped: ${p.caption}`,
        );
      }
      continue;
    }
    // One slide per hotel, as both docblocks promise. The page-level dedup runs
    // on the raw caption, so two spellings of the same name survive it; dedup on
    // the resolved hotel instead of trusting caption spelling.
    const id = hotel.hotel_id ?? normalizeHotelName(hotel.hotel_name);
    if (seenHotel.has(id)) continue;
    seenHotel.add(id);
    curated.push({ photo: p, hotel });
  }
  const slides =
    curated.length > 0
      ? curated.map(({ photo, hotel }) => ({
          src: photo.url,
          // 2026-07-25: the OPTIONAL {author, license} of this tile. These are
          // the deal's own carousel tiles, so a CC BY / CC BY-SA photo that
          // happens to carry a hotel name as its caption lands here as well as
          // in the gallery. Crediting it in only one of the two would still be
          // publishing it uncredited here. Null for practically every tile.
          credit: photo.credit ?? null,
          // The canonical hotel_name, never the raw caption: the two are equal
          // up to whitespace/case by construction of the filter above.
          name: hotel.hotel_name,
          stars: hotel.star_rating || 0,
          city: hotel.city || "",
          // hotelId lets an operator-curated "lead" photo open the carousel
          // (see leadHotelId below). Resolved via the same caption→hotel join.
          hotelId: hotel.hotel_id ?? null,
        }))
      : fallbackHotels(hotels, order)
          .map((h) => ({
            src: sfeerbeeldForHotel(h.hotel_id) || resolveSfeerHotelImage(h, "1920x1080"),
            // A photo credit belongs to curated gallery tiles only.
            credit: null,
            name: h.hotel_name,
            stars: h.star_rating || 3,
            city: h.city,
            hotelId: h.hotel_id ?? null,
          }))
          .filter((s) => s.src);

  // Dennis 2026-06-25: open the carousel on an operator-chosen "nicest" hotel
  // instead of always slide 0. The lead is a per-deal pin (deal-sfeerbeeld-lead
  // content map / future CMS field) resolved upstream; without one we keep the
  // existing slide-0 default so every other deal is byte-identical. The
  // gorgeous 4-5★ resort shots were previously buried at the end of the
  // backend hotel order (e.g. the Caesius Thermae pool on deal 292 sat at 14/15).
  // With an explicit per-deal order the fallback is already in that order and
  // slide 0 is the opener, so the lead pin is ignored.
  const hasOrder = curated.length === 0 && Array.isArray(order) && order.length > 0;
  const leadIndex =
    leadHotelId != null && !hasOrder
      ? Math.max(0, slides.findIndex((s) => s.hotelId === leadHotelId))
      : 0;

  const [active, setActive] = useState(leadIndex);
  const touchX = useRef(null);

  // Plain function (the React Compiler memoizes it). `go` is only called from
  // event handlers, never used in a hook dependency array, so it needs no
  // manual useCallback. The old useCallback tripped the React Compiler's
  // preserve-manual-memoization check once extra hooks were added above.
  const go = (dir) => {
    setActive((prev) => (prev + dir + slides.length) % slides.length);
  };

  if (slides.length === 0) return null;

  // Clamp during render (avoids setState-in-effect) so a changed slide set
  // can't index past the end.
  const safeActive = active < slides.length ? active : 0;
  const current = slides[safeActive];

  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className="mb-6">
      <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <span aria-hidden>📸</span> {t("deal.hotel_impressions_title")}
      </h3>
      <p className="mb-3 text-sm text-gray-500">
        {t("deal.hotel_impressions_subtitle")}
      </p>

      <div
        className="relative w-full h-[260px] sm:h-[420px] rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-100"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <Image
          key={current.src}
          src={current.src}
          alt={current.name || t("deal.hotel_photo_alt")}
          fill
          sizes="(max-width: 768px) 100vw, 1100px"
          className="object-cover"
        />
        {/* Bottom gradient + hotel-name label */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-4 sm:px-6 pt-10 pb-7 sm:pb-8 text-left pointer-events-none">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-lg sm:text-2xl font-semibold drop-shadow">
              {current.name}
            </span>
            {current.stars > 0 && <Stars count={current.stars} />}
          </div>
          {current.city && (
            <span className="text-white/90 text-sm sm:text-base drop-shadow">
              {current.city}
            </span>
          )}
        </div>
        {/* Verplichte fotovermelding als minitekst rechtsonder (Dennis 25-09-2026). */}
        <PhotoCreditMini credit={formatRequiredPhotoCredit(current.credit, t)} />

        {/* Slide counter */}
        <div className="absolute top-3 right-3 bg-black/55 text-white text-xs font-medium rounded-full px-2.5 py-1">
          {safeActive + 1} / {slides.length}
        </div>

        {/* Prev / next. Op mobiel op 1/3 hoogte: een hotelnaam over twee regels
            (plus sterren en stad) reikt in de 260px-carrousel tot boven het
            midden en viel dan half onder het linkerpijltje (04-10-2026). */}
        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label={t("common.prev")}
              className="absolute left-2 sm:left-3 top-1/3 sm:top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-gray-800 shadow"
            >
              &#10094;
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label={t("common.next")}
              className="absolute right-2 sm:right-3 top-1/3 sm:top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-gray-800 shadow"
            >
              &#10095;
            </button>
          </>
        )}

        {/* Dots */}
        {slides.length > 1 && slides.length <= 15 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {slides.map((s, i) => (
              <button
                key={`${s.src}-${i}`}
                type="button"
                onClick={() => setActive(i)}
                aria-label={t("deal.goto_photo_tpl", { n: i + 1 })}
                className={`h-2 rounded-full transition-all ${
                  i === safeActive ? "w-5 bg-white" : "w-2 bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
